import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isAuthConfigured } from '../lib/supabase';

const EMAIL_LIMIT_MESSAGE =
  'We\'re currently in limited beta and have reached our daily email limit. Please try again tomorrow, or sign in if you already have an account.';

function isEmailLimitError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('email rate limit') ||
    lower.includes('rate limit') ||
    lower.includes('unable to send') ||
    lower.includes('error sending') ||
    lower.includes('smtp') ||
    lower.includes('exceeded')
  );
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isConfigured: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'github') => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'TOKEN_REFRESHED') {
          console.debug('Auth token refreshed');
        }

        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    if (!supabase) throw new Error('Auth not configured');

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  const signUpWithEmail = async (email: string, password: string) => {
    if (!supabase) throw new Error('Auth not configured');

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      if (isEmailLimitError(error.message)) {
        throw new Error(EMAIL_LIMIT_MESSAGE);
      }
      throw error;
    }
  };

  const signInWithOAuth = async (provider: 'google' | 'github') => {
    if (!supabase) throw new Error('Auth not configured');

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    if (!supabase) throw new Error('Auth not configured');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    if (error) {
      if (isEmailLimitError(error.message)) {
        throw new Error(EMAIL_LIMIT_MESSAGE);
      }
      throw error;
    }
  };

  const signOut = async () => {
    if (!supabase) throw new Error('Auth not configured');

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isConfigured: isAuthConfigured(),
        signInWithEmail,
        signUpWithEmail,
        signInWithOAuth,
        resetPassword,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
